import React from 'react'
import styled, { css } from 'styled-components'
import { Link, navigate } from 'gatsby'

import Heading from '@components/Heading'
import ButtonArrow from '@components/Button/Button.Arrow'
import Image from '@components/Image'

import ThingsFeaturedMobile from './Things.Featured.Mobile'

import media from '@styles/media'

import { IThingNode } from '@typings'

/**
 * The top featured item. This displays a single thing that has been tagged
 * Featured: true in Contentful. This does not fit in the Grid below.
 */
const ThingsFeatured = ({ thing }: { thing: IThingNode }) => (
  <>
    <Frame>
      <StyledLink to={`/${thing.pathPrefix}/${thing.slug}`}>
        <Left>
          <SuperScript>Featured thing</SuperScript>
          <FeaturedTitle styles="h2" dark>
            {thing.title}
          </FeaturedTitle>
          <Excerpt>{thing.excerpt}</Excerpt>
          <ButtonArrow
            text="Read more"
            color="#000"
            as="div"
            onClick={() => navigate(`/${thing.pathPrefix}/${thing.slug}`)}
          />
        </Left>
        <Right>
          <Image src={thing.hero.Thing__Featured} />
        </Right>
      </StyledLink>
    </Frame>
    <ThingsFeaturedMobile thing={thing} />
  </>
)

export default ThingsFeatured

const limitToTwoLines = css`
  text-overflow: ellipsis;
  overflow-wrap: normal;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  display: -webkit-box;
  white-space: normal;
  overflow: hidden;

  ${media.phablet`
    -webkit-line-clamp: 3;
  `}
`

const Frame = styled.div`
  position: relative;
  display: flex;
  padding: 60px 0 100px;
  align-items: center;

  ${media.tablet`
    display: none;
  `}
`

const StyledLink = styled(Link)`
  display: flex;
  align-items: center;
  width: 100%;

  &:hover h3 {
    color: ${(p) => p.theme.colors.blue};
  }

  &:focus h3 {
    color: ${(p) => p.theme.colors.blue};
    text-decoration: underline;
  }
`

const FeaturedTitle = styled(Heading.h3)`
  margin-bottom: 15px;
  transition: color 0.3s ease-in-out;
`

const Left = styled.div`
  display: block;
  width: 100%;
  max-width: 500px;
  position: relative;
  z-index: 1;
`

const Right = styled.div`
  display: block;
  position: relative;
  width: 100%;
  height: 100%;
  right: -8%;
  transform: scale(1.088);

  ${media.tablet`
    display: none;
  `}
`

const SuperScript = styled.h2`
  position: relative;
  display: inline-block;
  margin-bottom: 65px;
  opacity: 0.25;
  font-weight: 400;
  color: #000;

  &::after {
    content: '';
    position: absolute;
    height: 1px;
    width: 30px;
    right: -42px;
    top: 11px;
    background: #000;
  }

  ${media.tablet`
    margin-bottom: 60px;
  `}
`

const Excerpt = styled.p`
  ${limitToTwoLines};
  font-size: 18px;
  margin-bottom: 30px;
  color: ${(p) => p.theme.colors.grey};
  display: ${(p) => (p.hasOverflow ? 'none' : 'box')};
  max-width: ${(p) => (p.narrow ? '415px' : '515px')};

  ${media.desktop`
    display: box;
  `}
`
